import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import {
  listSeedInboxes,
  createSeedInbox,
  updateSeedInbox,
  deleteSeedInbox,
  verifySeedInboxSecret,
  testSeedInboxConnection,
} from "@/lib/seed-inboxes.functions";
import { triggerSingleSeedInboxScan } from "@/lib/seed-scheduler.functions";
import { startGoogleOauthInbox } from "@/lib/seed-oauth.functions";
import { listTestMailTemplates, sendSeedInboxTestMail } from "@/lib/seed-test-mail.functions";
import { diagnoseGoogleOauthError, type OauthDiagnosis } from "@/lib/google-oauth-diagnosis";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ShieldCheck,
  Trash2,
  KeyRound,
  Mail,
  Plus,
  AlertTriangle,
  Settings2,
  KeyRound as KeyIcon,
  PlugZap,
  Lock,
  RefreshCw,
  ExternalLink,
  Check,
  Copy,
  QrCode,
  CheckCircle2,
  CircleAlert,
  XCircle,
  Send,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";

function formatRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 0) return "gerade eben";
  if (sec < 5) return "gerade eben";
  if (sec < 60) return `vor ${sec} Sek.`;
  const min = Math.round(sec / 60);
  if (min < 60) return `vor ${min} Min.`;
  const hrs = Math.round(min / 60);
  if (hrs < 24) return `vor ${hrs} Std.`;
  const days = Math.round(hrs / 24);
  return `vor ${days} Tg.`;
}

function isEncryptionKeyError(msg: string | undefined | null): boolean {
  if (!msg) return false;
  return /IMAP_ENCRYPTION_KEY/i.test(msg);
}

function friendlyError(e: Error): { title: string; description: string } {
  if (isEncryptionKeyError(e.message)) {
    return {
      title: "Master-Key fehlt",
      description:
        "IMAP_ENCRYPTION_KEY ist nicht konfiguriert (oder kürzer als 13 Zeichen). " +
        "Lege das Secret im Backend an (empfohlen 32+ Zeichen) und versuch es erneut.",
    };
  }
  return { title: "Fehler", description: e.message };
}

export const Route = createFileRoute("/_authenticated/seed-inboxes")({
  component: SeedInboxesPage,
});

const PROVIDERS = [
  { value: "gmail", label: "Gmail / Google Workspace" },
  { value: "outlook", label: "Outlook / Microsoft 365" },
  { value: "gmx", label: "GMX" },
  { value: "webde", label: "Web.de" },
  { value: "yahoo", label: "Yahoo" },
  { value: "mailde", label: "Mail.de" },
  { value: "tonline", label: "T-Online" },
  { value: "custom", label: "Andere (Custom IMAP)" },
];

function SeedInboxesPage() {
  // Tickt einmal pro Sekunde, damit "vor X Sek." live mitläuft —
  // unabhängig vom Refetch-Intervall der Query.
  const [, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Toast-Feedback nach OAuth-Callback (?oauth=ok|error)
  const [oauthDiagnosis, setOauthDiagnosis] = useState<OauthDiagnosis | null>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    const status = sp.get("oauth");
    if (!status) return;
    if (status === "ok") {
      toast.success("Gmail verbunden", {
        description: "Refresh-Token wurde verschlüsselt gespeichert.",
      });
    } else {
      const reason = sp.get("reason") ?? "unknown_error";
      const diag = diagnoseGoogleOauthError(reason);
      setOauthDiagnosis(diag);
      toast.error(diag.title, {
        description: diag.cause,
        action: {
          label: "Diagnose öffnen",
          onClick: () => {
            window.location.href = `/oauth-diagnosis?oauth=error&reason=${encodeURIComponent(reason)}`;
          },
        },
      });
    }
    // URL säubern, damit der Toast bei Reload nicht erneut feuert
    sp.delete("oauth");
    sp.delete("reason");
    const qs = sp.toString();
    window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
  }, []);

  const list = useServerFn(listSeedInboxes);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["seed-inboxes"],
    queryFn: () => list(),
    refetchInterval: 45_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
  const inboxes = data?.inboxes ?? [];
  const scanner = data?.scanner;
  const encryptionStatusKnown = data?.encryption_key_configured !== undefined;
  const encryptionKeyConfigured = data?.encryption_key_configured === true;
  const errorCount = inboxes.filter((i) => i.error_kind && i.error_kind !== "none").length;

  return (
    <div className="space-y-6">
      <OauthDiagnosisDialog
        diagnosis={oauthDiagnosis}
        onOpenChange={(open) => !open && setOauthDiagnosis(null)}
      />
      <PageHeader
        title="Seed-Inboxes"
        description="Test-Postfächer für Inbox-Placement-Monitoring. Passwörter werden AES-256-GCM-verschlüsselt mit einem serverseitigen Master-Key gespeichert und niemals an den Browser zurückgegeben."
        actions={
          <AddInboxDialog
            encryptionKeyConfigured={encryptionKeyConfigured}
            onDone={() => qc.invalidateQueries({ queryKey: ["seed-inboxes"] })}
          />
        }
      />

      <HelpPanel
        title="Hilfe: Seed-Inboxes & Google-Verbindungs-Wizard"
        what={
          <>
            Seed-Inboxes sind echte Test-Postfacher (Gmail, Outlook, GMX, ...), in die du deine
            Kampagnen mitsendest. Wir scannen sie automatisch und melden zuruck, ob deine Mail im
            <b> Posteingang</b>, im <b>Spam</b> oder in <b>Promotions</b> gelandet ist. Der
            Google-Wizard fuhrt dich durch die 4 Schritte, die Google fur den OAuth-Zugriff
            verlangt.
          </>
        }
        why={
          <>
            Ohne Seed-Inboxes weisst du nie sicher, wo deine Mails wirklich landen. Mit ihnen kannst
            du Spam-Probleme erkennen, <i>bevor</i> echte Empfanger sie sehen, und gezielt an
            Domain-Reputation, Inhalt oder Versand-Geschwindigkeit drehen.
          </>
        }
        how={[
          <>
            Klicke auf <b>Inbox hinzufugen</b> und wahle den Provider (Gmail OAuth empfohlen).
          </>,
          <>Beim Klick auf "Mit Google verbinden" offnet sich der 4-Schritte-Wizard.</>,
          <>
            <b>Schritt 1 - Consent Screen:</b> Nutzertyp "External", Status "Testing", App-Name und
            Support-Mail ausfullen.
          </>,
          <>
            <b>Schritt 2 - Scopes:</b> Drei Scopes freigeben (<code>userinfo.email</code>,{" "}
            <code>openid</code>, <code>gmail.readonly</code>).
          </>,
          <>
            <b>Schritt 3 - Testnutzer:</b> Genau die Gmail-Adresse, die du verbinden willst, als
            Test-User eintragen.
          </>,
          <>
            <b>Schritt 4 - Redirect-URI:</b> Die im Wizard angezeigte URL exakt im OAuth-Client
            hinterlegen (1-2 Min. Google-Cache abwarten).
          </>,
          <>
            Hakchen setzen, Wizard schliessen, Google-Login durchfuhren - fertig. Status sollte auf
            "verbunden" springen.
          </>,
        ]}
        tips={[
          <>
            Mehrere Tabs offen? Der Wizard synchronisiert seinen Fortschritt automatisch (Storage +
            BroadcastChannel).
          </>,
          <>Pro grossem Provider mindestens eine Seed-Inbox - sonst hast du blinde Flecken.</>,
          <>Scan-Intervall nicht unter 5 Min. setzen, sonst riskierst du Rate-Limits bei Google.</>,
          <>
            Bei "access_denied" zuerst Schritt 3 (Testnutzer) prufen - das ist der haufigste Fehler.
          </>,
          <>
            Master-Key (IMAP_ENCRYPTION_KEY) muss konfiguriert sein, sonst kannst du keine
            IMAP-Passworter speichern.
          </>,
        ]}
        glossary={[
          {
            term: "OAuth Consent Screen",
            def: "Die Einwilligungs-Maske, die Google deinem Test-Nutzer zeigt. Muss in der Google Cloud Console konfiguriert sein.",
          },
          {
            term: "Scope",
            def: "Berechtigung, die deine App von Google anfordert. 'gmail.readonly' = nur lesen, kein Schreiben.",
          },
          {
            term: "Testnutzer",
            def: "Solange die App im 'Testing'-Status ist, darf sich nur eine ausdrucklich eingetragene Adresse einloggen.",
          },
          {
            term: "Redirect-URI",
            def: "Die Adresse, an die Google nach dem Login zuruckspringt. Muss zeichengenau im OAuth-Client stehen.",
          },
          {
            term: "Seed-Inbox",
            def: "Test-Postfach, das wir scannen, um Inbox-/Spam-Platzierung zu messen.",
          },
          {
            term: "Inbox-Rate",
            def: "Anteil der Seed-Mails, die im Posteingang landen (nicht in Spam/Promotions). Ziel: > 90%.",
          },
          {
            term: "IMAP_ENCRYPTION_KEY",
            def: "Server-seitiger Master-Key (AES-256-GCM), mit dem IMAP-Passworter verschlusselt werden.",
          },
        ]}
      />

      {encryptionStatusKnown && !encryptionKeyConfigured && (
        <Alert variant="destructive">
          <Lock className="size-4" />
          <AlertTitle>Master-Key (IMAP_ENCRYPTION_KEY) fehlt oder ist zu kurz</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>
              Ohne diesen Schlüssel können neue Seed-Inbox-Passwörter nicht
              AES-256-GCM-verschlüsselt gespeichert werden, und bestehende Passwörter lassen sich
              beim Scan nicht entschlüsseln.
            </p>
            <ol className="list-decimal pl-5 space-y-1 text-sm">
              <li>
                Im Lovable-Chat (oder über die Backend-Einstellungen) das Secret{" "}
                <code className="font-mono">IMAP_ENCRYPTION_KEY</code> anlegen bzw. aktualisieren.
              </li>
              <li>
                Mindestens <strong>13 Zeichen</strong>, empfohlen 32+ – z. B. erzeugt mit{" "}
                <code className="font-mono">openssl rand -base64 32</code>.
              </li>
              <li>
                Nach dem Speichern diese Seite neu laden. Anschließend ggf. Passwörter über{" "}
                <strong>"Bearbeiten"</strong> erneut setzen, falls der Key gewechselt wurde.
              </li>
            </ol>
          </AlertDescription>
        </Alert>
      )}

      {scanner && !scanner.configured && inboxes.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="size-4" />
          <AlertTitle>IMAP-Scanner nicht konfiguriert</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>
              Es ist keine <code className="font-mono">scanner_url</code> hinterlegt. Cloudflare
              Workers können kein IMAP direkt sprechen — ohne externen Scanner werden alle aktiven
              Inboxes beim Scan nur als "geprüft" markiert, aber keine Mails abgerufen.
            </p>
            <Button asChild size="sm" variant="outline">
              <Link to="/seed-scheduler">
                <Settings2 className="size-4 mr-1" />
                Scheduler-Einstellungen öffnen
              </Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {scanner?.configured && errorCount > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="size-4" />
          <AlertTitle>
            {errorCount} {errorCount === 1 ? "Inbox" : "Inboxes"} mit Scan-Fehler
          </AlertTitle>
          <AlertDescription>
            Siehe Status-Spalte unten. Häufige Ursachen: falsches App-Passwort, abgelaufene
            Credentials oder geblockte IMAP-Verbindung beim Provider.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="size-5 text-primary" />
            Sicherheits-Hinweise
          </CardTitle>
          <CardDescription>
            Nutze app-spezifische Passwörter (z. B. Gmail App Password, GMX "POP3/IMAP-Passwort").
            Wenn der Master-Key (IMAP_ENCRYPTION_KEY) rotiert wird, müssen alle Passwörter neu
            eingetragen werden. Für Rollbacks einzelner Änderungen nutze den History-/Revert-Button
            oben im Lovable-Chat.
          </CardDescription>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Inboxes ({inboxes.length})</CardTitle>
          <CardDescription>
            Diese Postfächer werden später automatisch per IMAP abgefragt, um zu prüfen, ob
            Kampagnen-Mails im Posteingang, Spam oder Promotions landen.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Lade…</p>
          ) : inboxes.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground">
              <Mail className="size-10 mx-auto mb-3 opacity-40" />
              Noch keine Seed-Inboxes. Klick oben auf <strong>"Inbox hinzufügen"</strong>.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground border-b">
                  <tr>
                    <th className="py-2 pr-4">E-Mail</th>
                    <th className="py-2 pr-4">Provider</th>
                    <th className="py-2 pr-4">IMAP-Host</th>
                    <th className="py-2 pr-4">Region</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Letzte Prüfung</th>
                    <th className="py-2 pr-4 text-right">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {inboxes.map((i) => (
                    <InboxRow key={i.id} inbox={i} scannerConfigured={!!scanner?.configured} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === "active")
    return (
      <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30">aktiv</Badge>
    );
  if (status === "paused") return <Badge variant="secondary">pausiert</Badge>;
  return <Badge variant="destructive">Fehler</Badge>;
}

/**
 * Drei-Schritt-Statusblock pro Inbox, damit auf einen Blick klar ist, an
 * welcher Stelle die Verbindung hängt:
 *   1. OAuth verbunden  (nur für oauth_google)
 *   2. Token gespeichert + Ablaufzeit
 *   3. Letzter IMAP-Scan (ok / Fehler / nie gelaufen)
 */
function ConnectionStatusBlock({ inbox }: { inbox: InboxRowData }) {
  const isOauth = inbox.auth_method === "oauth_google";
  const oauth = inbox.oauth;
  const tokenConnected = !!oauth?.connected;
  const expiresAt = oauth?.access_token_expires_at ? new Date(oauth.access_token_expires_at) : null;
  const tokenExpired = expiresAt ? expiresAt.getTime() < Date.now() : false;

  const hasError = !!inbox.error_kind && inbox.error_kind !== "none";
  const scanOk = !!inbox.last_checked_at && !hasError && inbox.status === "active";
  const scanNever = !inbox.last_checked_at;

  return (
    <ul className="mt-2 space-y-1 text-xs">
      {isOauth && (
        <>
          <StatusStep
            ok={tokenConnected}
            okLabel={`OAuth verbunden${oauth?.google_email_masked ? ` (${oauth.google_email_masked})` : ""}`}
            failLabel='OAuth nicht verbunden — auf "Mit Google verbinden" klicken'
          />
          <StatusStep
            ok={tokenConnected && !tokenExpired}
            okLabel={
              expiresAt
                ? `Token gespeichert · läuft ab ${formatRelativeTime(expiresAt.toISOString())}`
                : "Token gespeichert"
            }
            failLabel={
              !tokenConnected
                ? "Kein Refresh-Token gespeichert"
                : "Access-Token abgelaufen — wird beim nächsten Scan refreshed"
            }
            warn={tokenConnected && tokenExpired}
          />
        </>
      )}
      <StatusStep
        ok={scanOk}
        warn={scanNever}
        okLabel={
          inbox.last_checked_at
            ? `IMAP-Scan ok · ${formatRelativeTime(inbox.last_checked_at)}`
            : "IMAP-Scan ok"
        }
        failLabel={
          scanNever
            ? "Noch kein IMAP-Scan gelaufen"
            : `IMAP-Scan fehlgeschlagen${inbox.error_kind ? ` (${inbox.error_kind})` : ""}`
        }
      />
    </ul>
  );
}

function StatusStep({
  ok,
  warn,
  okLabel,
  failLabel,
}: {
  ok: boolean;
  warn?: boolean;
  okLabel: string;
  failLabel: string;
}) {
  if (ok) {
    return (
      <li className="flex items-start gap-1.5 text-emerald-600">
        <CheckCircle2 className="size-3.5 mt-0.5 shrink-0" />
        <span>{okLabel}</span>
      </li>
    );
  }
  if (warn) {
    return (
      <li className="flex items-start gap-1.5 text-amber-500">
        <CircleAlert className="size-3.5 mt-0.5 shrink-0" />
        <span>{failLabel}</span>
      </li>
    );
  }
  return (
    <li className="flex items-start gap-1.5 text-destructive">
      <XCircle className="size-3.5 mt-0.5 shrink-0" />
      <span>{failLabel}</span>
    </li>
  );
}

type InboxRowData = {
  id: string;
  email_masked: string;
  display_name: string | null;
  provider: string;
  imap_host: string | null;
  imap_port: number;
  imap_tls: boolean;
  imap_username_masked: string | null;
  password_display: string;
  region: string | null;
  status: string;
  last_checked_at: string | null;
  last_error: string | null;
  error_kind?: string;
  auth_method?: string | null;
  oauth?: {
    connected: boolean;
    provider: string | null;
    google_email_masked: string | null;
    access_token_expires_at: string | null;
    scope: string | null;
    token_updated_at: string | null;
  };
};

function ErrorDetail({ kind, rawError }: { kind: string | undefined; rawError: string | null }) {
  if (!rawError) return null;

  const presets: Record<
    string,
    { label: string; icon: React.ReactNode; hint: string; cta?: React.ReactNode }
  > = {
    scanner_missing: {
      label: "Scanner fehlt",
      icon: <PlugZap className="size-3.5" />,
      hint: "Keine scanner_url hinterlegt — IMAP kann nicht abgefragt werden.",
      cta: (
        <Link to="/seed-scheduler" className="underline underline-offset-2 hover:text-foreground">
          Scheduler konfigurieren →
        </Link>
      ),
    },
    auth: {
      label: "Login-Fehler",
      icon: <KeyIcon className="size-3.5" />,
      hint: "IMAP-Login abgelehnt. Prüfe E-Mail-Adresse + App-Passwort und ob IMAP beim Provider aktiviert ist.",
    },
    decrypt: {
      label: "Passwort nicht entschlüsselbar",
      icon: <Lock className="size-3.5" />,
      hint: "IMAP_ENCRYPTION_KEY hat sich vermutlich geändert. Passwort neu setzen.",
    },
    connection: {
      label: "Verbindungsfehler",
      icon: <PlugZap className="size-3.5" />,
      hint: "IMAP-Host nicht erreichbar (Timeout, DNS, TLS). Prüfe Host/Port/TLS.",
    },
    scanner_http: {
      label: "Scanner-Fehler",
      icon: <AlertTriangle className="size-3.5" />,
      hint: "Externer Scanner hat mit Fehler-Status geantwortet.",
    },
    other: {
      label: "Fehler",
      icon: <AlertTriangle className="size-3.5" />,
      hint: "Unbekannter Fehler beim letzten Scan.",
    },
  };
  const p = presets[kind ?? "other"] ?? presets.other;

  return (
    <div className="mt-1.5 space-y-1">
      <Badge variant="destructive" className="gap-1 font-normal">
        {p.icon}
        {p.label}
      </Badge>
      <div className="text-xs text-muted-foreground leading-snug">{p.hint}</div>
      {p.cta && <div className="text-xs">{p.cta}</div>}
      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer hover:text-foreground">Original-Meldung</summary>
        <code className="mt-1 block break-all rounded bg-muted px-1.5 py-1 font-mono text-[11px]">
          {rawError}
        </code>
      </details>
    </div>
  );
}

function InboxRow({
  inbox,
  scannerConfigured,
}: {
  inbox: InboxRowData;
  scannerConfigured: boolean;
}) {
  const qc = useQueryClient();
  const verifyFn = useServerFn(verifySeedInboxSecret);
  const deleteFn = useServerFn(deleteSeedInbox);
  const scanFn = useServerFn(triggerSingleSeedInboxScan);

  const verify = useMutation({
    mutationFn: () => verifyFn({ data: { id: inbox.id } }),
    onSuccess: (r) => {
      if (r.ok)
        toast.success("Entschlüsselung OK", {
          description:
            "Verschlüsseltes Passwort konnte mit dem aktuellen Master-Key gelesen werden.",
        });
      else toast.error("Fehler beim Entschlüsseln", { description: r.error });
      qc.invalidateQueries({ queryKey: ["seed-inboxes"] });
    },
    onError: (e: Error) => {
      const f = friendlyError(e);
      toast.error(f.title, { description: f.description });
    },
  });

  const del = useMutation({
    mutationFn: () => deleteFn({ data: { id: inbox.id } }),
    onSuccess: () => {
      toast.success("Inbox gelöscht");
      qc.invalidateQueries({ queryKey: ["seed-inboxes"] });
    },
    onError: (e: Error) => {
      const f = friendlyError(e);
      toast.error(f.title, { description: f.description });
    },
  });

  const rescan = useMutation({
    mutationFn: () => scanFn({ data: { id: inbox.id } }),
    onSuccess: () => {
      toast.success("Scan gestartet", {
        description: `IMAP-Scan für ${inbox.email_masked} wurde sofort angestoßen.`,
      });
      qc.invalidateQueries({ queryKey: ["seed-inboxes"] });
    },
    onError: (e: Error) => {
      const f = friendlyError(e);
      toast.error(isEncryptionKeyError(e.message) ? f.title : "Scan fehlgeschlagen", {
        description: f.description,
      });
    },
  });

  const hasError = !!inbox.error_kind && inbox.error_kind !== "none";
  const canRescan = hasError && inbox.error_kind !== "scanner_missing" && scannerConfigured;

  return (
    <tr className="border-b last:border-0">
      <td className="py-3 pr-4">
        <div className="font-medium">{inbox.email_masked}</div>
        {inbox.display_name && (
          <div className="text-xs text-muted-foreground">{inbox.display_name}</div>
        )}
      </td>
      <td className="py-3 pr-4">{inbox.provider}</td>
      <td className="py-3 pr-4 font-mono text-xs">
        <div>
          {inbox.imap_host}:{inbox.imap_port}
          {inbox.imap_tls ? " TLS" : ""}
        </div>
        <div className="text-muted-foreground" title="IMAP-Benutzer (maskiert)">
          user: {inbox.imap_username_masked}
        </div>
        <div
          className="text-muted-foreground"
          title="Passwort liegt AES-256-GCM-verschlüsselt im Vault — wird nie an den Browser zurückgegeben"
        >
          pw: <span className="select-none tracking-widest">{inbox.password_display}</span>
        </div>
      </td>
      <td className="py-3 pr-4">{inbox.region ?? "—"}</td>
      <td className="py-3 pr-4 max-w-xs">
        <StatusBadge status={inbox.status} />
        <ConnectionStatusBlock inbox={inbox} />
        {!scannerConfigured && !inbox.last_error && inbox.status === "active" && (
          <div className="mt-1.5 text-xs text-muted-foreground">
            <Badge variant="secondary" className="gap-1 font-normal">
              <PlugZap className="size-3.5" />
              Wartet auf Scanner
            </Badge>
          </div>
        )}
        <ErrorDetail kind={inbox.error_kind} rawError={inbox.last_error} />
      </td>
      <td className="py-3 pr-4 text-xs text-muted-foreground">
        {inbox.last_checked_at ? (
          <span title={new Date(inbox.last_checked_at).toLocaleString()}>
            aktualisiert {formatRelativeTime(inbox.last_checked_at)}
          </span>
        ) : (
          "nie"
        )}
      </td>
      <td className="py-3 pr-4 text-right space-x-2 whitespace-nowrap">
        {canRescan && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => rescan.mutate()}
            disabled={rescan.isPending}
            title="IMAP-Scan über alle aktiven Seed-Inboxes sofort anstoßen"
          >
            <RefreshCw className={`size-4 mr-1 ${rescan.isPending ? "animate-spin" : ""}`} />
            Erneut scannen
          </Button>
        )}
        {inbox.auth_method === "oauth_google" && inbox.oauth?.connected && (
          <TestMailDialog inbox={inbox} />
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={() => verify.mutate()}
          disabled={verify.isPending}
        >
          <KeyRound className="size-4 mr-1" />
          Key testen
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="ghost" className="text-destructive">
              <Trash2 className="size-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Inbox löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                {inbox.email_masked} wird endgültig entfernt. Das verschlüsselte Passwort wird
                gelöscht. Diese Aktion kann nur über den Lovable-History-Revert rückgängig gemacht
                werden — und auch dann nur die Datenbankstruktur, nicht das Passwort selbst.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction onClick={() => del.mutate()}>Löschen</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </td>
    </tr>
  );
}

function TestMailDialog({ inbox }: { inbox: InboxRowData }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [analysisId, setAnalysisId] = useState<string>("__default__");
  const [lastResult, setLastResult] = useState<{
    subject: string;
    subject_tag: string;
  } | null>(null);

  const listFn = useServerFn(listTestMailTemplates);
  const sendFn = useServerFn(sendSeedInboxTestMail);
  const scanFn = useServerFn(triggerSingleSeedInboxScan);

  const templatesQ = useQuery({
    queryKey: ["test-mail-templates"],
    queryFn: () => listFn(),
    enabled: open,
    staleTime: 60_000,
  });

  const send = useMutation({
    mutationFn: async () => {
      const sendRes = await sendFn({
        data: {
          inbox_id: inbox.id,
          analysis_id: analysisId === "__default__" ? null : analysisId,
        },
      });
      if (!sendRes.ok) throw new Error(sendRes.error);
      // Direkt nach erfolgreichem Versand den IMAP-Scan dieser Inbox triggern.
      // Fehler hier schlucken — Versand war erfolgreich, Scan ist nur "nice to have".
      let scanOk = false;
      let scanError: string | null = null;
      try {
        await scanFn({ data: { id: inbox.id } });
        scanOk = true;
      } catch (e) {
        scanError = e instanceof Error ? e.message : String(e);
      }
      return { sendRes, scanOk, scanError };
    },
    onSuccess: ({ sendRes, scanOk, scanError }) => {
      if (sendRes.ok) {
        setLastResult({ subject: sendRes.subject, subject_tag: sendRes.subject_tag });
        toast.success("Test-Mail gesendet", {
          description: scanOk
            ? `Subject: ${sendRes.subject} · IMAP-Scan wurde direkt angestoßen.`
            : `Subject: ${sendRes.subject} · Scan konnte nicht gestartet werden${scanError ? `: ${scanError}` : ""}.`,
        });
        qc.invalidateQueries({ queryKey: ["seed-inboxes"] });
      }
    },
    onError: (e: Error) => {
      toast.error("Test-Mail fehlgeschlagen", { description: e.message });
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          title="Test-Mail an diese Gmail-Inbox senden und Empfangs-Scan direkt starten"
        >
          <Send className="size-4 mr-1" />
          Test senden
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Test-Mail an {inbox.email_masked}</DialogTitle>
          <DialogDescription>
            Sendet eine Mail von dieser Gmail-Inbox an sich selbst (via Gmail-API) und startet
            unmittelbar danach den IMAP-Empfangstest, damit das Placement (Inbox / Promotions /
            Spam) erkannt wird.
          </DialogDescription>
        </DialogHeader>

        {!inbox.oauth?.scope?.includes("gmail.send") && (
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertTitle>Sende-Berechtigung fehlt</AlertTitle>
            <AlertDescription>
              Der gespeicherte OAuth-Token wurde nur mit Lese-Rechten erteilt (
              <code>gmail.readonly</code>). Verbinde die Inbox erneut, damit Google zusätzlich um{" "}
              <code>gmail.send</code> bittet.
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Vorlage (HTML-Analyse)</Label>
            <Select value={analysisId} onValueChange={setAnalysisId}>
              <SelectTrigger>
                <SelectValue placeholder="Default-Test-Mail" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__default__">Default-Test-Mail (kurzer Body)</SelectItem>
                {templatesQ.data?.templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                    {typeof t.spam_score === "number" ? ` · Spam-Score ${t.spam_score}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Subject erhält automatisch einen eindeutigen Tag <code>[seed-test xxxxxx]</code>,
              damit der Scan die Mail wiedererkennt.
            </p>
          </div>

          {lastResult && (
            <div className="rounded border bg-muted/40 p-2 text-xs">
              <div className="font-medium">Zuletzt gesendet:</div>
              <div className="font-mono break-all">{lastResult.subject}</div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={send.isPending}>
            Schließen
          </Button>
          <Button onClick={() => send.mutate()} disabled={send.isPending}>
            <Send className={`size-4 mr-1 ${send.isPending ? "animate-pulse" : ""}`} />
            {send.isPending ? "Sende & scanne…" : "Senden & scannen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddInboxDialog({
  encryptionKeyConfigured,
  onDone,
}: {
  encryptionKeyConfigured: boolean;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [provider, setProvider] = useState("gmail");
  const [authMethod, setAuthMethod] = useState<"app_password" | "oauth_google">("oauth_google");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [region, setRegion] = useState("");
  const [customHost, setCustomHost] = useState("");
  const [customPort, setCustomPort] = useState(993);
  const [customTls, setCustomTls] = useState(true);
  const [customUser, setCustomUser] = useState("");
  const createFn = useServerFn(createSeedInbox);
  const startOauthFn = useServerFn(startGoogleOauthInbox);
  const testFn = useServerFn(testSeedInboxConnection);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    checks: Array<{
      name: string;
      ok: boolean;
      detail: string;
      latency_ms?: number;
      error_kind?: string;
      kind?: string;
      hint?: string;
      suggestions?: Array<{ label: string; port: number; tls: boolean; reason: string }>;
    }>;
    host: string | null;
    port: number;
    note?: string;
  } | null>(null);

  // Zod-Schema für Client-seitige Validierung (spiegelt das Server-Schema)
  const formSchema = z
    .object({
      email: z.string().trim().email("Ungültige E-Mail-Adresse").max(255),
      password: z
        .string()
        .min(4, "Passwort zu kurz (mind. 4 Zeichen)")
        .max(512, "Passwort zu lang"),
      displayName: z.string().trim().max(120).optional(),
      region: z.string().trim().max(40).optional(),
      customHost: z.string().trim().optional(),
      customPort: z.number().int().min(1).max(65535),
      customUser: z.string().trim().max(255).optional(),
    })
    .superRefine((val, ctx) => {
      if (provider === "custom") {
        if (!val.customHost || val.customHost.length === 0) {
          ctx.addIssue({
            code: "custom",
            path: ["customHost"],
            message: "IMAP-Host ist erforderlich für Custom-Provider",
          });
        } else if (
          !/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i.test(val.customHost)
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["customHost"],
            message: "Ungültiger Hostname (z. B. imap.example.com)",
          });
        }
      }
    });

  function validateForApp(): { ok: boolean; data?: z.infer<typeof formSchema> } {
    const parsed = formSchema.safeParse({
      email,
      password,
      displayName,
      region,
      customHost,
      customPort,
      customUser,
    });
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? "_");
        if (!errs[key]) errs[key] = issue.message;
      }
      setFieldErrors(errs);
      return { ok: false };
    }
    setFieldErrors({});
    return { ok: true, data: parsed.data };
  }

  // Bei Eingabe Test-Ergebnis verwerfen (Daten haben sich geändert)
  useEffect(() => {
    setTestResult(null);
  }, [email, password, provider, customHost, customPort]);

  // Auto-switch: für Nicht-Gmail-Provider gibt's nur App-Passwort
  useEffect(() => {
    if (provider !== "gmail" && authMethod === "oauth_google") {
      setAuthMethod("app_password");
    }
  }, [provider, authMethod]);

  const startOauth = useMutation({
    mutationFn: () =>
      startOauthFn({
        data: { email, display_name: displayName || undefined, region: region || undefined },
      }),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error("OAuth-Start fehlgeschlagen", { description: r.error });
        return;
      }
      // Browser zu Google weiterleiten
      window.location.href = r.url;
    },
    onError: (e: Error) => toast.error("Fehler", { description: e.message }),
  });

  const create = useMutation({
    mutationFn: () => {
      const v = validateForApp();
      if (!v.ok) {
        throw new Error("Bitte fehlerhafte Felder korrigieren.");
      }
      return createFn({
        data: {
          email,
          provider: provider as
            | "gmail"
            | "outlook"
            | "gmx"
            | "webde"
            | "yahoo"
            | "mailde"
            | "tonline"
            | "custom",
          password,
          display_name: displayName || undefined,
          region: region || undefined,
          imap_host: provider === "custom" ? customHost : undefined,
          imap_port: provider === "custom" ? customPort : undefined,
          imap_username: provider === "custom" && customUser ? customUser : undefined,
        },
      });
    },
    onSuccess: (r) => {
      if (!r.ok) {
        const f = friendlyError(new Error(r.error));
        toast.error(f.title, { description: f.description });
        return;
      }
      toast.success("Inbox hinzugefügt", {
        description: "Passwort wurde verschlüsselt gespeichert.",
      });
      setOpen(false);
      setEmail("");
      setPassword("");
      setDisplayName("");
      setRegion("");
      setCustomHost("");
      setCustomUser("");
      setTestResult(null);
      setFieldErrors({});
      onDone();
    },
    onError: (e: Error) => {
      const f = friendlyError(e);
      toast.error(f.title, { description: f.description });
    },
  });

  const testConn = useMutation({
    mutationFn: () => {
      const v = validateForApp();
      if (!v.ok) throw new Error("Bitte fehlerhafte Felder korrigieren.");
      return testFn({
        data: {
          email,
          provider: provider as
            | "gmail"
            | "outlook"
            | "gmx"
            | "webde"
            | "yahoo"
            | "mailde"
            | "tonline"
            | "custom",
          password,
          imap_host: provider === "custom" ? customHost : undefined,
          imap_port: provider === "custom" ? customPort : undefined,
          imap_tls: provider === "custom" ? customTls : undefined,
          imap_username: provider === "custom" && customUser.trim() ? customUser.trim() : undefined,
        },
      });
    },
    onSuccess: (r) => {
      setTestResult(r);
      if (r.ok) {
        toast.success("IMAP-Login OK", {
          description: "Echter LOGIN-Roundtrip gegen den IMAP-Server war erfolgreich.",
        });
      } else {
        toast.error("Verbindungstest fehlgeschlagen", {
          description: "Siehe Details im Dialog.",
        });
      }
    },
    onError: (e: Error) => {
      const f = friendlyError(e);
      toast.error(f.title, { description: f.description });
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          disabled={!encryptionKeyConfigured}
          title={!encryptionKeyConfigured ? "IMAP_ENCRYPTION_KEY zuerst konfigurieren" : undefined}
        >
          <Plus className="size-4 mr-1" />
          Inbox hinzufügen
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Seed-Inbox hinzufügen</DialogTitle>
          <DialogDescription>
            Gmail: per Google-OAuth verbinden (empfohlen) — kein App-Passwort, kein externer
            IMAP-Scanner nötig. Andere Provider: IMAP-Passwort wird AES-256-GCM-verschlüsselt.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label>Provider</Label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROVIDERS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {provider === "gmail" && (
            <div className="grid gap-2">
              <Label>Verbindungsmethode</Label>
              <Select
                value={authMethod}
                onValueChange={(v) => setAuthMethod(v as typeof authMethod)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="oauth_google">
                    Google OAuth (empfohlen — direkt im Worker, kein externer Scanner)
                  </SelectItem>
                  <SelectItem value="app_password">
                    App-Passwort (klassisch, benötigt externen IMAP-Scanner)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid gap-2">
            <Label>E-Mail</Label>
            <Input
              type="email"
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seed01@gmail.com"
              aria-invalid={!!fieldErrors.email}
            />
            {fieldErrors.email && <p className="text-xs text-destructive">{fieldErrors.email}</p>}
          </div>

          {authMethod === "app_password" && (
            <div className="grid gap-2">
              <Label>Passwort / App-Passwort</Label>
              <Input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={!!fieldErrors.password}
              />
              {fieldErrors.password && (
                <p className="text-xs text-destructive">{fieldErrors.password}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Für Gmail: ein App-Passwort aus den Google-Sicherheitseinstellungen, nicht das
                normale Konto-Passwort.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label>Anzeigename (optional)</Label>
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Seed Gmail #1"
              />
            </div>
            <div className="grid gap-2">
              <Label>Region (optional)</Label>
              <Input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="DE" />
            </div>
          </div>

          {provider === "custom" && (
            <div className="grid gap-3 p-3 rounded-md border bg-muted/30">
              <div className="grid grid-cols-3 gap-3">
                <div className="grid gap-2 col-span-2">
                  <Label>IMAP-Host</Label>
                  <Input
                    value={customHost}
                    onChange={(e) => setCustomHost(e.target.value)}
                    placeholder="imap.example.com"
                    aria-invalid={!!fieldErrors.customHost}
                  />
                  {fieldErrors.customHost && (
                    <p className="text-xs text-destructive">{fieldErrors.customHost}</p>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label>Port</Label>
                  <Input
                    type="number"
                    min={1}
                    max={65535}
                    value={customPort}
                    onChange={(e) => setCustomPort(Number(e.target.value) || 993)}
                    aria-invalid={!!fieldErrors.customPort}
                  />
                  {fieldErrors.customPort && (
                    <p className="text-xs text-destructive">{fieldErrors.customPort}</p>
                  )}
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={customTls}
                  onChange={(e) => setCustomTls(e.target.checked)}
                />
                <span>
                  Impliziter TLS (IMAPS) — für Port 993 anlassen; bei 143/STARTTLS deaktivieren.
                </span>
              </label>
              <div className="grid gap-2">
                <Label>IMAP-Benutzer (optional, sonst = E-Mail)</Label>
                <Input value={customUser} onChange={(e) => setCustomUser(e.target.value)} />
              </div>
            </div>
          )}

          {authMethod === "app_password" && testResult && (
            <div
              className={`rounded-md border p-3 text-xs space-y-1.5 ${
                testResult.ok
                  ? "border-emerald-500/30 bg-emerald-500/5"
                  : "border-destructive/40 bg-destructive/5"
              }`}
            >
              <div className="flex items-center gap-2 font-medium">
                {testResult.ok ? (
                  <CheckCircle2 className="size-4 text-emerald-600" />
                ) : (
                  <XCircle className="size-4 text-destructive" />
                )}
                <span>
                  Verbindungstest:{" "}
                  {testResult.ok ? `OK · ${testResult.host}:${testResult.port}` : "Fehlgeschlagen"}
                </span>
              </div>
              <ul className="space-y-1 pl-1">
                {testResult.checks.map((c) => (
                  <li key={c.name} className="flex items-start gap-2">
                    {c.ok ? (
                      <CheckCircle2 className="size-3.5 mt-0.5 text-emerald-600 shrink-0" />
                    ) : (
                      <XCircle className="size-3.5 mt-0.5 text-destructive shrink-0" />
                    )}
                    <div className="flex-1 space-y-0.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-medium">{c.name}</span>
                        {!c.ok && c.error_kind && (
                          <span className="rounded bg-destructive/10 text-destructive px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                            {c.error_kind}
                          </span>
                        )}
                        {typeof c.latency_ms === "number" && (
                          <span className="text-muted-foreground/70 text-[10px]">
                            {c.latency_ms} ms
                          </span>
                        )}
                      </div>
                      <div className="text-muted-foreground">{c.detail}</div>
                      {!c.ok && c.hint && (
                        <div className="rounded border border-amber-500/30 bg-amber-500/5 px-2 py-1 text-amber-900 dark:text-amber-200">
                          <span className="font-medium">Tipp:</span> {c.hint}
                        </div>
                      )}
                      {!c.ok && c.suggestions && c.suggestions.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {c.suggestions.map((s) => {
                            const canApply = provider === "custom";
                            return (
                              <button
                                key={`${s.port}-${s.tls}`}
                                type="button"
                                disabled={!canApply}
                                title={
                                  canApply
                                    ? `${s.reason} — übernimmt Port ${s.port}, TLS ${s.tls ? "on" : "off"}.`
                                    : "Nur für 'Custom'-Provider verfügbar."
                                }
                                onClick={() => {
                                  setCustomPort(s.port);
                                  setCustomTls(s.tls);
                                  setTestResult(null);
                                  toast.info(
                                    `Übernommen: Port ${s.port}, TLS ${s.tls ? "on" : "off"}`,
                                    {
                                      description: "Klicke erneut auf 'Verbindung testen'.",
                                    },
                                  );
                                }}
                                className="rounded border border-primary/30 bg-primary/5 px-2 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Übernehmen: {s.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              {testResult.note && (
                <p className="pt-1 text-muted-foreground italic">{testResult.note}</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Abbrechen
          </Button>
          {authMethod === "oauth_google" ? (
            <Button
              onClick={() => setWizardOpen(true)}
              disabled={startOauth.isPending || !email || !encryptionKeyConfigured}
            >
              {startOauth.isPending ? "Weiterleitung…" : "Mit Google verbinden"}
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => testConn.mutate()}
                disabled={testConn.isPending || !email || !password || !encryptionKeyConfigured}
              >
                <PlugZap className={`size-4 mr-1 ${testConn.isPending ? "animate-pulse" : ""}`} />
                {testConn.isPending ? "Teste…" : "Verbindung testen"}
              </Button>
              <Button
                onClick={() => create.mutate()}
                disabled={create.isPending || !email || !password || !encryptionKeyConfigured}
              >
                {create.isPending ? "Speichere…" : "Verschlüsselt speichern"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
      <GoogleConnectWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        email={email}
        isStarting={startOauth.isPending}
        onConfirm={() => {
          setWizardOpen(false);
          startOauth.mutate();
        }}
      />
    </Dialog>
  );
}

function OauthDiagnosisDialog({
  diagnosis,
  onOpenChange,
}: {
  diagnosis: OauthDiagnosis | null;
  onOpenChange: (open: boolean) => void;
}) {
  const open = diagnosis !== null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{diagnosis?.title ?? "OAuth-Fehler"}</DialogTitle>
          <DialogDescription>{diagnosis?.cause}</DialogDescription>
        </DialogHeader>
        {diagnosis && (
          <div className="space-y-4 text-sm">
            <div>
              <div className="font-medium mb-2">So behebst du das:</div>
              <ol className="list-decimal pl-5 space-y-1.5 text-muted-foreground">
                {diagnosis.steps.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ol>
            </div>
            <div className="rounded-md border bg-muted/40 p-3">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                Roh-Antwort von Google ({diagnosis.code})
              </div>
              <code className="text-xs break-all">{diagnosis.rawReason}</code>
            </div>
            {diagnosis.docsUrl && (
              <a
                href={diagnosis.docsUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-primary underline"
              >
                Google-Dokumentation öffnen
              </a>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const REDIRECT_URI = "https://deliverability-delight.lovable.app/api/public/oauth/google/callback";

// Version des persistierten Wizard-Schemas. Bei strukturellen Änderungen
// (neue Felder im Payload, andere Step-Anzahl, neue Semantik) hochzählen –
// alte Einträge werden dann ignoriert und einmalig aus localStorage entfernt.
const WIZARD_STORAGE_VERSION = 1;

const WIZARD_STEPS: Array<{
  title: string;
  description: string;
  url: string;
  urlLabel: string;
}> = [
  {
    title: "1. OAuth Consent Screen prüfen",
    description:
      "Öffne die Zielgruppen-Einstellung. Stelle sicher, dass der Nutzertyp auf 'External' steht und der Publishing-Status auf 'Testing'. ('Internal' erlaubt nur Adressen der eigenen Workspace-Domain.) App-Name, Support-Mail und Entwickler-Mail müssen ausgefüllt sein.",
    url: "https://console.cloud.google.com/apis/credentials/consent",
    urlLabel: "OAuth consent screen öffnen",
  },
  {
    title: "2. Scopes freigeben",
    description:
      "Im Consent Screen unter 'Scopes' müssen diese drei Einträge stehen: .../auth/userinfo.email, openid und https://www.googleapis.com/auth/gmail.readonly. Falls noch nicht vorhanden, 'Add or remove scopes' klicken.",
    url: "https://console.cloud.google.com/apis/credentials/consent/edit",
    urlLabel: "Scopes bearbeiten",
  },
  {
    title: "3. Testnutzer eintragen",
    description:
      "Trage die unten genannte Gmail-Adresse als Test user ein — ohne diesen Schritt blockt Google mit 'access_denied'.",
    url: "https://console.cloud.google.com/apis/credentials/consent",
    urlLabel: "Test users-Liste öffnen",
  },
  {
    title: "4. Redirect-URI im OAuth-Client hinterlegen",
    description:
      "Öffne deinen OAuth-2.0-Web-Client (Credentials → OAuth 2.0 Client IDs) und trage unter 'Authorized redirect URIs' die unten stehende URL exakt ein (mit https, ohne Slash am Ende). 1–2 Min. Google-Cache.",
    url: "https://console.cloud.google.com/apis/credentials",
    urlLabel: "Credentials öffnen",
  },
];

function CopyableField({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-2">
        <code className="flex-1 rounded-md border bg-muted px-2 py-1.5 text-xs font-mono break-all">
          {value}
        </code>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        </Button>
      </div>
    </div>
  );
}

function GoogleConnectWizard({
  open,
  onOpenChange,
  email,
  isStarting,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  isStarting: boolean;
  onConfirm: () => void;
}) {
  const [step, setStep] = useState(0);
  const [checked, setChecked] = useState<boolean[]>([false, false, false, false]);
  const [userId, setUserId] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [gcpProjectId, setGcpProjectId] = useState("");
  // Zeitstempel des zuletzt von DIESEM Tab geschriebenen Stands. Wird beim
  // Empfang eines storage-Events verglichen: nur jüngere Updates gewinnen.
  const lastUpdatedAtRef = useRef(0);
  const lastProjectUpdatedAtRef = useRef(0);
  // Letzter beobachteter Broadcast-Reset (Zeitstempel). So ignorieren wir
  // unseren eigenen Reset und reagieren nur auf Neuere aus anderen Tabs.
  const lastResetAtRef = useRef(0);
  // BroadcastChannel ergänzt das storage-Event: storage feuert nicht zuverlässig
  // in allen Edge-Cases (z. B. derselbe Tab, Private-Mode-Quirks, manche
  // mobilen WebViews). BroadcastChannel deckt diese Lücken ab.
  const channelRef = useRef<BroadcastChannel | null>(null);
  // Hinweis, wenn ein anderer Tab den Wizard aktualisiert/zurückgesetzt hat.
  const [externalUpdate, setExternalUpdate] = useState<{
    kind: "sync" | "reset";
    step: number;
    at: number;
  } | null>(null);

  // Hinweis automatisch nach 5 s ausblenden.
  useEffect(() => {
    if (!externalUpdate) return;
    const t = window.setTimeout(() => setExternalUpdate(null), 5000);
    return () => window.clearTimeout(t);
  }, [externalUpdate]);

  // BroadcastChannel pro Nutzer öffnen / schließen.
  useEffect(() => {
    if (!userId || typeof window === "undefined" || typeof BroadcastChannel === "undefined") {
      return;
    }
    const ch = new BroadcastChannel(`gcw:v${WIZARD_STORAGE_VERSION}:${userId}`);
    channelRef.current = ch;
    return () => {
      ch.close();
      if (channelRef.current === ch) channelRef.current = null;
    };
  }, [userId]);

  // Helper: Nachricht senden, Fehler schlucken.
  const postChannel = (msg: unknown) => {
    try {
      channelRef.current?.postMessage(msg);
    } catch {
      // ignore
    }
  };

  // Aktuellen Nutzer einmalig laden – Key für die Persistenz
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => {
      if (!cancelled) setUserId(data.user?.id ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Versioniertes Key-Schema. Bei Schema-Änderungen WIZARD_STORAGE_VERSION
  // erhöhen – alte Einträge werden dann ignoriert und einmalig aufgeräumt.
  const keyPrefix = `gcw:v${WIZARD_STORAGE_VERSION}`;
  const storageKey = userId
    ? `${keyPrefix}:${userId}:${(email || "_no_email_").toLowerCase()}`
    : null;
  const projectKey = userId ? `${keyPrefix}:${userId}:gcp_project` : null;
  const resetBroadcastKey = userId ? `${keyPrefix}:${userId}:reset` : null;

  // Einmaliges Aufräumen veralteter Wizard-Keys (andere Version, oder
  // Pre-Versionierung ohne `v<n>`-Segment).
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stale: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k || !k.startsWith("gcw:")) continue;
        if (k.startsWith(`${keyPrefix}:`)) continue;
        stale.push(k);
      }
      stale.forEach((k) => localStorage.removeItem(k));
    } catch {
      // ignore
    }
  }, [keyPrefix]);

  // GCP-Projekt-ID nutzerweit laden / speichern
  useEffect(() => {
    if (!projectKey || typeof window === "undefined") return;
    const raw = localStorage.getItem(projectKey);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as { value?: string; updatedAt?: number };
      if (typeof parsed?.value === "string") {
        setGcpProjectId(parsed.value);
        if (typeof parsed.updatedAt === "number") {
          lastProjectUpdatedAtRef.current = parsed.updatedAt;
        }
        return;
      }
    } catch {
      // alter String-Eintrag – einmalig migrieren
    }
    setGcpProjectId(raw);
  }, [projectKey]);
  useEffect(() => {
    if (!projectKey || typeof window === "undefined") return;
    if (gcpProjectId) {
      const updatedAt = Date.now();
      lastProjectUpdatedAtRef.current = updatedAt;
      localStorage.setItem(projectKey, JSON.stringify({ value: gcpProjectId, updatedAt }));
      postChannel({ type: "project", value: gcpProjectId, updatedAt });
    } else {
      lastProjectUpdatedAtRef.current = 0;
      localStorage.removeItem(projectKey);
      postChannel({ type: "project", value: "", updatedAt: Date.now() });
    }
  }, [projectKey, gcpProjectId]);

  const withProject = (url: string): string => {
    const id = gcpProjectId.trim();
    if (!id) return url;
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}project=${encodeURIComponent(id)}`;
  };

  // Beim Öffnen: gespeicherten Fortschritt laden (nicht zurücksetzen)
  useEffect(() => {
    if (!open || !storageKey || typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as {
          step?: number;
          checked?: boolean[];
          updatedAt?: number;
        };
        if (Array.isArray(parsed.checked) && parsed.checked.length === WIZARD_STEPS.length) {
          setChecked(parsed.checked.map(Boolean));
        }
        if (
          typeof parsed.step === "number" &&
          parsed.step >= 0 &&
          parsed.step < WIZARD_STEPS.length
        ) {
          setStep(parsed.step);
        }
        if (typeof parsed.updatedAt === "number") {
          lastUpdatedAtRef.current = parsed.updatedAt;
        }
      } else {
        setStep(0);
        setChecked([false, false, false, false]);
      }
    } catch {
      // ignorieren – defekter Eintrag
    }
    setHydrated(true);
  }, [open, storageKey]);

  // Bei jeder Änderung persistieren (erst nach Hydration, damit wir nichts überschreiben)
  useEffect(() => {
    if (!open || !hydrated || !storageKey || typeof window === "undefined") return;
    try {
      const updatedAt = Date.now();
      lastUpdatedAtRef.current = updatedAt;
      localStorage.setItem(storageKey, JSON.stringify({ step, checked, updatedAt }));
      postChannel({
        type: "progress",
        email: (email || "_no_email_").toLowerCase(),
        step,
        checked,
        updatedAt,
      });
    } catch {
      // localStorage voll / disabled – egal
    }
  }, [open, hydrated, storageKey, step, checked]);

  // Cross-Tab-Sync: storage-Event feuert in ANDEREN Tabs, wenn dort
  // localStorage geschrieben wird. So bleiben alle offenen Wizard-Instanzen
  // auf demselben Stand und springen nie in einen veralteten Schritt zurück.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.storageArea !== window.localStorage) return;
      // Fortschritt für diesen Wizard
      if (storageKey && e.key === storageKey) {
        if (e.newValue == null) {
          setStep(0);
          setChecked([false, false, false, false]);
          lastUpdatedAtRef.current = 0;
          return;
        }
        try {
          const parsed = JSON.parse(e.newValue) as {
            step?: number;
            checked?: boolean[];
            updatedAt?: number;
          };
          // Nur übernehmen, wenn die Änderung neuer ist als unser letzter Stand –
          // so gewinnt bei parallelen Tabs immer der jüngste Schreibvorgang.
          const incoming = typeof parsed.updatedAt === "number" ? parsed.updatedAt : 0;
          if (incoming && incoming <= lastUpdatedAtRef.current) return;
          lastUpdatedAtRef.current = incoming;
          if (Array.isArray(parsed.checked) && parsed.checked.length === WIZARD_STEPS.length) {
            setChecked(parsed.checked.map(Boolean));
          }
          if (
            typeof parsed.step === "number" &&
            parsed.step >= 0 &&
            parsed.step < WIZARD_STEPS.length
          ) {
            setStep(parsed.step);
          }
          const incomingStep =
            typeof parsed.step === "number" && parsed.step >= 0 && parsed.step < WIZARD_STEPS.length
              ? parsed.step
              : step;
          setExternalUpdate({ kind: "sync", step: incomingStep, at: Date.now() });
        } catch {
          // ignorieren
        }
      }
      // GCP-Projekt-ID
      if (projectKey && e.key === projectKey) {
        // Auch die Projekt-ID trägt einen Zeitstempel; neuere Tabs gewinnen.
        const raw = e.newValue;
        if (raw == null) {
          setGcpProjectId("");
          lastProjectUpdatedAtRef.current = 0;
          return;
        }
        try {
          const parsed = JSON.parse(raw) as { value?: string; updatedAt?: number };
          if (typeof parsed?.value === "string") {
            const incoming = typeof parsed.updatedAt === "number" ? parsed.updatedAt : 0;
            if (incoming && incoming <= lastProjectUpdatedAtRef.current) return;
            lastProjectUpdatedAtRef.current = incoming;
            setGcpProjectId(parsed.value);
            return;
          }
        } catch {
          // alter String-Eintrag (Pre-Migration)
        }
        setGcpProjectId(raw);
      }
      // Broadcast-Reset: jeder offene Wizard-Tab leert seinen lokalen Stand
      // und löscht den eigenen storageKey. Eigener Reset wird ignoriert.
      if (resetBroadcastKey && e.key === resetBroadcastKey) {
        const ts = e.newValue ? Number(e.newValue) : 0;
        if (!ts || ts <= lastResetAtRef.current) return;
        lastResetAtRef.current = ts;
        setStep(0);
        setChecked([false, false, false, false]);
        lastUpdatedAtRef.current = 0;
        if (storageKey) {
          try {
            localStorage.removeItem(storageKey);
          } catch {
            // ignore
          }
        }
        setExternalUpdate({ kind: "reset", step: 0, at: Date.now() });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storageKey, projectKey, resetBroadcastKey]);

  // BroadcastChannel-Listener: dieselbe Merge-Logik wie beim storage-Event,
  // nur über einen zweiten Kanal. Doppelte Updates sind dank Zeitstempel-
  // Vergleich harmlos (älteres wird verworfen).
  useEffect(() => {
    const ch = channelRef.current;
    if (!ch) return;
    const ownEmail = (email || "_no_email_").toLowerCase();
    const onMessage = (e: MessageEvent) => {
      const msg = e.data as
        | { type: "progress"; email: string; step: number; checked: boolean[]; updatedAt: number }
        | { type: "project"; value: string; updatedAt: number }
        | { type: "reset"; at: number }
        | null;
      if (!msg || typeof msg !== "object") return;

      if (msg.type === "progress" && msg.email === ownEmail) {
        const incoming = typeof msg.updatedAt === "number" ? msg.updatedAt : 0;
        if (!incoming || incoming <= lastUpdatedAtRef.current) return;
        lastUpdatedAtRef.current = incoming;
        if (Array.isArray(msg.checked) && msg.checked.length === WIZARD_STEPS.length) {
          setChecked(msg.checked.map(Boolean));
        }
        if (typeof msg.step === "number" && msg.step >= 0 && msg.step < WIZARD_STEPS.length) {
          setStep(msg.step);
        }
        setExternalUpdate({ kind: "sync", step: msg.step ?? 0, at: Date.now() });
        return;
      }

      if (msg.type === "project") {
        const incoming = typeof msg.updatedAt === "number" ? msg.updatedAt : 0;
        if (!incoming || incoming <= lastProjectUpdatedAtRef.current) return;
        lastProjectUpdatedAtRef.current = incoming;
        setGcpProjectId(typeof msg.value === "string" ? msg.value : "");
        return;
      }

      if (msg.type === "reset") {
        const ts = typeof msg.at === "number" ? msg.at : 0;
        if (!ts || ts <= lastResetAtRef.current) return;
        lastResetAtRef.current = ts;
        setStep(0);
        setChecked([false, false, false, false]);
        lastUpdatedAtRef.current = 0;
        if (storageKey && typeof window !== "undefined") {
          try {
            localStorage.removeItem(storageKey);
          } catch {
            // ignore
          }
        }
        setExternalUpdate({ kind: "reset", step: 0, at: Date.now() });
      }
    };
    ch.addEventListener("message", onMessage);
    return () => ch.removeEventListener("message", onMessage);
    // userId triggert das Neuaufsetzen des Channels weiter oben; wir hängen
    // hier zusätzlich an email und storageKey, weil sich daraus die
    // Empfänger-Selektion bzw. das Aufräumziel ändert.
  }, [userId, email, storageKey]);

  const current = WIZARD_STEPS[step];
  const total = WIZARD_STEPS.length;
  const isLast = step === total - 1;
  const allDone = checked.every(Boolean);

  const toggle = (i: number) => setChecked((prev) => prev.map((v, idx) => (idx === i ? !v : v)));

  const resetProgress = () => {
    setStep(0);
    setChecked([false, false, false, false]);
    if (storageKey && typeof window !== "undefined") {
      localStorage.removeItem(storageKey);
    }
  };

  // Reset über alle offenen Tabs – schreibt einen Broadcast-Eintrag mit
  // Zeitstempel, den alle Wizard-Instanzen via storage-Event aufgreifen.
  const resetProgressEverywhere = () => {
    resetProgress();
    if (resetBroadcastKey && typeof window !== "undefined") {
      const ts = Date.now();
      lastResetAtRef.current = ts;
      try {
        localStorage.setItem(resetBroadcastKey, String(ts));
      } catch {
        // localStorage voll / disabled – egal
      }
      postChannel({ type: "reset", at: ts });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Verbindungs-Assistent: Google OAuth</DialogTitle>
          <DialogDescription>
            Schritt {step + 1} von {total} — wir führen dich einmal durch die Google-Cloud-Console,
            damit der Login beim ersten Versuch klappt.
          </DialogDescription>
        </DialogHeader>

        {/* Fortschritt */}
        <div className="flex gap-1.5">
          {WIZARD_STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                i < step ? "bg-primary" : i === step ? "bg-primary/60" : "bg-muted"
              }`}
            />
          ))}
        </div>
        {externalUpdate && (
          <div
            role="status"
            aria-live="polite"
            className="rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-xs text-foreground"
          >
            {externalUpdate.kind === "reset"
              ? `Ein anderer Tab hat den Wizard zurückgesetzt — du bist jetzt bei Schritt ${externalUpdate.step + 1}.`
              : `Ein anderer Tab hat den Fortschritt aktualisiert — übernommen bis Schritt ${externalUpdate.step + 1}.`}
          </div>
        )}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Fortschritt wird automatisch gespeichert.</span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={resetProgress}
              className="underline hover:text-foreground"
            >
              Von vorne beginnen
            </button>
            <button
              type="button"
              onClick={resetProgressEverywhere}
              className="underline hover:text-foreground"
              title="Setzt diesen Wizard in allen offenen Tabs zurück"
            >
              In allen Tabs zurücksetzen
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <h3 className="font-semibold text-base">{current.title}</h3>
            <p className="text-sm text-muted-foreground">{current.description}</p>
          </div>

          {/* Step-spezifische Felder zum Kopieren */}
          {step === 2 && (
            <CopyableField
              value={email || "(noch keine E-Mail eingegeben)"}
              label="Test user (genau diese Adresse eintragen)"
            />
          )}
          {step === 3 && <CopyableField value={REDIRECT_URI} label="Authorized redirect URI" />}

          <DeepLinkField url={withProject(current.url)} label={current.urlLabel} />
          <div className="grid gap-1.5 rounded-md border p-3">
            <Label htmlFor="gcp-project-id" className="text-xs">
              Google-Cloud-Projekt-ID (optional – Links springen dann direkt ins richtige Projekt)
            </Label>
            <Input
              id="gcp-project-id"
              value={gcpProjectId}
              onChange={(e) => setGcpProjectId(e.target.value)}
              placeholder="z. B. my-mailer-1234"
              autoComplete="off"
            />
            <p className="text-[11px] text-muted-foreground">
              Findest du oben links in der Google Cloud Console neben dem Projektnamen. Wird pro
              Nutzer gespeichert.
            </p>
          </div>

          <label className="flex items-start gap-2 rounded-md border bg-muted/30 p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={checked[step]}
              onChange={() => toggle(step)}
              className="mt-0.5"
            />
            <span className="text-sm">
              Erledigt — Schritt {step + 1} ist in Google Cloud gespeichert.
            </span>
          </label>
        </div>

        <DialogFooter className="flex sm:justify-between gap-2">
          <Button
            variant="ghost"
            onClick={() => (step === 0 ? onOpenChange(false) : setStep((s) => s - 1))}
          >
            {step === 0 ? "Abbrechen" : "Zurück"}
          </Button>
          {isLast ? (
            <Button
              onClick={() => {
                resetProgress();
                onConfirm();
              }}
              disabled={!allDone || !email || isStarting}
              title={
                !allDone
                  ? "Bitte alle Schritte abhaken"
                  : !email
                    ? "E-Mail im vorherigen Dialog eingeben"
                    : undefined
              }
            >
              {isStarting ? "Weiterleitung…" : "Jetzt mit Google verbinden"}
            </Button>
          ) : (
            <Button onClick={() => setStep((s) => s + 1)} disabled={!checked[step]}>
              Weiter
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeepLinkField({ url, label }: { url: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=8&data=${encodeURIComponent(url)}`;
  return (
    <div className="space-y-2 rounded-md border bg-muted/30 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button asChild size="sm">
          <a href={url} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="size-3.5 mr-1.5" />
            {label}
          </a>
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => {
            navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? <Check className="size-3.5 mr-1.5" /> : <Copy className="size-3.5 mr-1.5" />}
          {copied ? "Kopiert" : "Link kopieren"}
        </Button>
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" size="sm" variant="outline">
              <QrCode className="size-3.5 mr-1.5" />
              QR-Code
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3 flex flex-col items-center gap-2">
            <img
              src={qrSrc}
              alt={`QR-Code: ${label}`}
              width={200}
              height={200}
              className="rounded bg-white"
            />
            <p className="text-xs text-muted-foreground text-center max-w-[200px]">
              Mit dem Handy scannen, um die Seite dort zu öffnen.
            </p>
          </PopoverContent>
        </Popover>
      </div>
      <code className="block text-[11px] font-mono break-all text-muted-foreground">{url}</code>
    </div>
  );
}
